import {
  Translations,
  LeadStage,
  InventoryStatus,
  VehicleStatus,
  LeadType,
  InitialTouchPoint,
  SourceOfBusiness,
  OwnerUser,
  AccountType,
} from "..";

export interface AzureAdSettings {
  clientId: string;
  clientSecret: string;
  tenantId: string;
}

export interface OutlookSettings {
  clientId: string;
  clientSecret: string;
  tenantId: string;
}
export interface PDFSettings {
  primaryColor: string;
  paidCapitalAmount: number;
  registrationNumber: string;
  companyNameEnglish: string;
  companyNameArabic: string;
  showInsurancePlanRejectedByCustomer?: boolean;
  showInsuranceProposalForm?: boolean;
  logoUrl?: string;
  showDisclaimerOnConfigurationHasBooking?: boolean;
  wappPdfLogo?: string;
  serviceCenterUrl?: string;
  alghanimLogoUrl?: string;
}

export interface ApplicationSettings {
  email: string;
  applicationBaseUrl: string;
  crmBaseUrl: string;
  tollFreeNumber: string;
  receiptNoPrefix?: number;
  discountOtpPhone: string;
  preBookingOtpPhone: string;
  isCustomerEmailMandatoryForEnquiry?: boolean;
  appointmentTime?: {
    start: string;
    end: string;
  };
  newProductArriavedEmails: string;
  bookingReportEmails: string;
  isDisableConfigUpdateChangeColor?: boolean;
  useAlreadyExistingDiscount?: boolean;
  disableCustomerCreation?: boolean;
  accessoryPushedToAutolineEmails: string;
  weekends?: string[];
  productArrivalUserTypes: string[];
  brandColor: string;
  availabilityStatusUpdateReportEmails: string;
  accessoryReportEmails: string;
  serviceAdvisorBodyShopLeadEmails: string;
}

export interface UserbackSettings {
  userbackToken?: string;
}

export interface SocialMediaLink {
  name: string;
  url?: string;
  imageUrl: string;
}
export interface OneDriveSettingsData {
  clientId: string;
  clientSecret: string;
  tenantId: string;
  configured: boolean;
  orderInfoUploadPath: string;
  accessoriesInfoUploadPath: string;
  userEmail: string;
  paymentInfoUploadPath: string;
  paymentRequestType: string;
  invoiceInfoUploadPath: string;
  invoiceRequestType: string;
  companyCode: string;
  vehicleFileUploadPath?: string;
}

export interface AutolineSettings {
  baseUrl: string;
  brandName: string;
  apiKey: string;
  apiEncryptionKey: string;
  defaultSalesmanHrSystemId: string;
  serviceDetailsBaseUrl?: string;
  serviceDetailsApiKey?: string;
  defaultSourceOfBusiness: string;
  campaignDiscountAutolineCode?: string;
  accessoriesDiscountAutolineCode?: string;
  cashBackDiscountAutolineCode?: string;
  couponDiscountAutolineCode?: string;
  adjustmentsAutolineCode?: string;
  listingPriceAutolineCode?: string;
  customerBrandName?: string;
  financeCommissionMenuCode?: string;
  vehicleFromYear: string;
  vinDiscountAutolineCode?: string;
  extraAddOnMenuCode?: string;
}

export interface OorjitSettings {
  baseUrl: string;
  apiKey: string;
  // websiteId: string;
  // subsiteId: string;
  // languageIdEnglish: string;
  // languageIdArabic: string;
}

export interface ShortenUrlSettings {
  apiUrl: string;
  apiKey: string;
  domain: string;
}

export interface ExternalLeadsSettings {
  fb: ExternalLeadSourceSettings;
  linkedin: ExternalLeadSourceSettings;
  tiktok: ExternalLeadSourceSettings;
}

export interface SMSSettings {
  account: {
    configured?: boolean;
    accountId: string;
    username: string;
    password: string;
    senderId: string;
  };
  sendSms: boolean;
  liveOtp: boolean;
  debugOtp: string;
  otpLength: number;
}

export interface AiApiSettings {
  apiUrl: string;
  username: string;
  password: string;
  brandName: string;
}

export interface TimeZoneSettings {
  timeZone: string;
  timeZoneOffsetInHours: number;
  timeZoneOffset: string;
}

export interface EmailSettings {
  configured?: boolean;
  sendMail?: boolean;
  host?: string;
  username?: string;
  password?: string;
  port?: number;
  from?: string;
  ssl?: boolean;
  sesConfigName?: string;
}

export interface GeneralSettings {
  timeZone: string;
  currency: string;
  franchiseName: string;
  logoUrl?: string;
  alghanimLogoUrl?: string;
  removeVisibilityGroupChildrenOnDelete?: boolean;
}

export interface ExternalLeadSourceSettings {
  baseUrl: string;
}

export interface LeadLostApprovalSettings {
  leadStage: string;
}

export interface Expiry {
  value: number;
  unit: "Days" | "Hours" | "Minutes";
}

export interface QuotationSettings {
  expiry: Expiry;
  vinNumberExpiry: Expiry;
  sendVinExpiryNotificationBefore: Expiry;
  monthEndExpiryEnabled?: boolean;
  isCustomerEmailMandatoryForQuotation: boolean;
}

export interface BookingSettings {
  expiry: Expiry;
  vehicleStatus?: InventoryStatus[];
}

export interface PreBookingSettings {
  expiry: Expiry;
  sameCalendarMonthOptionEnabled?: boolean;
  campaignDifferentiatorTimeline?: Expiry;
}

interface DiscountValueSettings {
  discountType: "percentage" | "fixed";
  discountValue: number;
}

export interface DiscountSettings {
  accessoryMaximumDiscount: DiscountValueSettings;
  accessoryDiscountSlab?: DiscountSettingsSlab[];
  approverTypes: string[];
  beyondSlabApproverTypes: string[];
}

export interface DiscountSettingsSlab {
  from?: number;
  to?: number;
  discount?: number;
}

export interface SiblingBrand {
  baseUrl: string;
  brandName: string;
}

export interface CloudFrontSettings {
  url: string;
  s3Bucket: string;
  region: string;
}

export interface Settings {
  azureAd?: AzureAdSettings;
  outlook?: OutlookSettings;
  oneDrive: OneDriveSettingsData;
  cloudFront?: CloudFrontSettings;
  oorjit?: OorjitSettings;
  externalLeads?: ExternalLeadsSettings;
  autoline?: AutolineSettings;
  sms?: SMSSettings;
  timeZone?: TimeZoneSettings;
  currency: string;
  franchiseName: string;
  logoUrl: string;
  alghanimLogoUrl: string;
  removeVisibilityGroupChildrenOnDelete?: boolean;
  email?: EmailSettings;
  quotation?: QuotationSettings;
  booking?: BookingSettings;
  preBooking?: PreBookingSettings;
  leadAssignment?: LeadAssignmentSettings;
  companyAssignment?: CompanyAssigneeSettings;
  leadActivityAssignment?: LeadActivitySettingsData;
  siblingBrands: SiblingBrand[];
  shortenUrl?: ShortenUrlSettings;
  pdf?: PDFSettings;
  application?: ApplicationSettings;
  discount?: DiscountSettings;
  notification?: NotificationSettings;
  socialMedia?: SocialMediaLink[];
  leadLostApproval?: LeadLostApprovalSettings;
  aiApiConfig?: AiApiSettings;
  followUp?: FollowUpGapTimeSettings;
  ownerForUnassignedEntities: OwnerForUnassignedEntities;
  onlineLeadsVisibilityGroups: OnlineLeadsVisibilityGroups;
  leadStageFollowupActivities?: FollowupSettings;
  whatsappApi?: WhatsAppSettings;
  invoice?: InvoiceSettings;
  tradeInAccount?: TradeInAccountSettings;
  vehicleReportSettings?: VehicleReportSettings;
  ocr?: OCRSettings;
  activity?: ActivitySettings;
  userback?: UserbackSettings;
  whatsappLeadAssignment?: WhatsappLeadAssignmentSettings;
  packagePriceCalculationSettings?: PackagePriceCalculationSettingsData;
  serviceAdvisorSettings?: ServiceAdvisorSettings;
  bodyShopSettings?: BodyShopSettings;
  deliveryFollowUp?: DeliveryFollowUpGapTimeSettings;
  toleranceSettings:RequirementSettings
}

export interface FollowupSettings {
  stages?: FollowupActivitiesOfStage[];
}

export interface FollowupActivitiesOfStage {
  stage?: LeadStage;
  followupActivities: AllActivityTypes[];
  followupOnReschedule: boolean;
  noteTypes?: NoteTypes[];
}

export interface SystemTask {
  _id: string;
  name: string;
  type: string;
  nextRunAt: Date;
  priority: number;
  repeatInterval: string;
  lockedAt: string | Date;
  lastRunAt: string | Date;
  lastFinishedAt: string | Date;
  failCount?: number;
  failReason?: string;
  failedAt?: string | Date;
}

export interface ITemplateKeyItem {
  key: string;
  name: string;
  appNotificationTemplateOptions?: {
    notifyCrmAgent: boolean;
    notifyCustomer: boolean;
    notifySalesPerson: boolean;
    whatsappCustomer: boolean;
    whatsAppTemplateInfo: string;
  };
}

export interface NotificationTemplate {
  key: string;
  templates: INotificationTemplate;
}

export interface INotificationTemplate {
  email: INotificationTemplateItem;
  sms: INotificationTemplateItem;
  notification: INotificationTemplateItem;
  wapp: WappNotificationTemplate;
}

export interface INotificationTemplateItem {
  _id: string;
  isDeleted: boolean;
  isActive: boolean;
  key: string;
  messageTemplate: string;
  subject: string;
  placeholders: IPlaceholders[];
  createdAt: Date;
  updatedAt: Date;
}

export interface WappNotificationTemplate extends INotificationTemplateItem {
  approvedReferenceTemplate?: string;
  isDocumentTemplate?: boolean;
  useBrochureTemplate?: boolean;
}

export interface IPlaceholders {
  displayName: string;
  value: string;
}

export interface ICreateTemplate {
  key: string;
  messageTemplate: string;
  subject?: string;
  isActive: boolean;
}

export interface ICreateWappTemplate extends ICreateTemplate {
  approvedReferenceTemplate?: string;
  isDocumentTemplate?: boolean;
  useBrochureTemplate?: boolean;
}

export interface LeadAssignmentSettings {
  crmAgentTypes: string[];
  salesPersonTypes: string[];
  leadAssigneeHeadTypes?:string[];
  groupManagerTypes?:string[];
  leadManagerTypes?:string[];
  teleCallerTypes?:string[];
  noOfDaysToTakeCRMAgentForAutoAssign?: number;
}

export interface CompanyAssigneeSettings {
  assigneeTypes: string[];
}

export interface WhatsappLeadAssignmentSettings {
  userType: string;
  accountTypes: string[];
}

export interface LeadActivitySettingsData {
  requestACallBack: string[];
  showroomVisit: string[];
  testDrive: string[];
  default: string[];
  otherActivity: string[];
  noOfDaysToAllowBackdatedActivity?: number;
  isCustomerAvailableInShowroomRequiredForTestDrive?: boolean;
}
export interface PackagePriceCalculationSettingsData {
  insurance: PackageCalculationMethod;
  servicePackage: PackageCalculationMethod;
  customerCare: PackageCalculationMethod;
  extendedWarranty: PackageCalculationMethod;
  packagePriceAutolineCode?: string;
}
export interface NotificationSettings {
  smsEnabledTemplates: string[];
  appNotificationEnabledTemplates: string[];
  emailEnabledTemplates: string[];
  ccEnabledEvents: string[];
  enableCCEmailToCrmAgentForAssignSalesPerson?: boolean;
  enableCCEmailToCrmAgentForTestDrive?: boolean;
  enableCCEmailToCrmAgentForShowroomVisit?: boolean;
  appNotifications: AppNotificationSettings[];
  appoinmentReminderTriggerInMinutes?: number;
  convertedToEnquiryEmailSmsRecipientsUserTypes?: string[];
  campaignEmailRecipientsUserType?: string;
}

export interface AppNotificationSettings {
  event: string;
  enabled: true;
  notifySalesPerson?: boolean;
  notifyCrmAgent?: boolean;
  notifyCustomer?: boolean;
  whatsappCustomer?: boolean;
  whatsAppTemplateInfo?: string;
  receiverUserTypes: string[];
}

export interface OwnerForUnassignedEntities {
  lead: OwnersAndVisibilityGroups;
  configuration?: OwnersAndVisibilityGroups;
  activity?: OwnersAndVisibilityGroups;
  customer?: OwnerLastUserVisibilityGroup;
  report?: OwnersAndVisibilityGroups;
  payment?: OwnersAndVisibilityGroups;
  organization?: OwnerLastUserVisibilityGroup;
}

export interface OwnersAndVisibilityGroups {
  salesPerson?: string[];
  crmAgent?: string[];
}

export interface OwnerLastUserVisibilityGroup
  extends OwnersAndVisibilityGroups {
  lastSalesPerson?: string[];
  lastCrmAgent?: string[];
}

export interface OnlineLeadsVisibilityGroups {
  leadTypes?: LeadTypesSobAssociations[];
}

export interface LeadTypesSobAssociations {
  leadType: string;
  sourceofBusinesses: SobVisibilityGroups[];
}

export interface SobVisibilityGroups {
  sourceOfBusiness: string;
  sobVisibilityGroups?: string[];
}

export interface ServiceAdvisorSettings {
  leadTypes: LeadType[];
  initialTouchPoints: InitialTouchPoint[];
  sourceOfBusinesses: SourceOfBusiness[];
  staffMembers: OwnerUser[];
}

export interface BodyShopSettings {
  leadTypes: LeadType[];
  initialTouchPoints: InitialTouchPoint[];
  sourceOfBusinesses: SourceOfBusiness[];
  staffMembers: OwnerUser[];
}

export interface District {
  name: string;
  _id?: string;
  governorate: string;
  otherName: string;
  translations?: { name?: Translations };
}
export interface IGovernorate {
  name: string;
  _id?: string;
  translations?: { name?: Translations };
}
export interface ITag {
  name: string;
  _id: string;
}

export interface FollowUpGapTimeSettings {
  leadFollowUpGapTimeInHours: number;
  quotationFollowUpGapTimeInHours: number;
  enquiryFollowUpGapTimeInHours: number;
  bookingFollowUpGapTimeInHours: number;
  overdueTimeInMinutes?: number;
}

export interface DeliveryFollowUpGapTimeSettings {
  firstFollowUpGapTimeInHours: number;
  secondFollowUpGapTimeInHours: number;
  deliveryEscalationUserTypes: AccountType[];
}

export enum AllActivityTypes {
  changeLog = "changeLog",
  note = "note",
  activity = "activity",
  call = "call",
  meeting = "meeting",
  deadline = "deadline",
  task = "task",
  requestACallBack = "requestACallBack",
  scheduleATestDrive = "scheduleATestDrive",
  showroomAppointment = "showroomAppointment",
  file = "file",
  timeOff = "timeOff",
  vehicleTimeOff = "vehicleTimeOff",
  followup = "followup",
}

export enum FollowUpActivityTypes {
  note = "note",
  call = "call",
  meeting = "meeting",
  deadline = "deadline",
  task = "task",
  requestACallBack = "requestACallBack",
  scheduleATestDrive = "scheduleATestDrive",
  showroomAppointment = "showroomAppointment",
}

export enum NoteTypes {
  documents = "documents",
  finance = "finance",
  freeText = "freeText",
}

export interface LeadStageField {
  displayName: string;
  type: string;
  value: string;
  group: string;
}

export interface WhatsAppSettings {
  botId: string;
  authToken: string;
  service: string;
  apiUrl: string;
  templateInfo: string;
}
export interface IOcrDocumentTag {
  name: string;
  accountType: string[];
  _id: string;
}

export interface InvoiceSettings {
  enabled: boolean;
  invoiceNoPrefix?: string;
  invoiceLockExpiry: Expiry;
  createInvoiceWithDueAmount?: boolean;
}

export interface TradeInAccountSettings {
  accountNumber: string;
  cashBackMenuCode: string;
}

export interface VehicleReportSettings {
  modelBackgroundColor: string;
  yearBackgroundColor: string;
  variantBackgroundColor: string;
  headingBackgroundColor: string;
  borderColor: string;
  headingFontColor: string;
  exteriorBackgroundColor: string;
}

export interface OCRSettings {
  bulkUploadS3BucketName: string;
  bulkUploadS3Region: string;
  bulkUploadS3AccessKeyId: string;
  bulkUploadS3SecretAccessKey: string;
  configured: boolean;
  notificationEmail?: string;
}

export interface RequirementSettings {
  value:number
}

export interface Holiday {
  _id?: string;
  fromDate?: Date;
  toDate?: Date;
  numberOfDays?: number;
  holidayTitle?: string;
}
export interface ActivitySettings {
  activityMinimumDuration: Expiry;
}

export interface Checklist {
  _id?: string;
  name: string;
  vehicleStatus?: VehicleStatus[];
}

export enum PackageCalculationMethod {
  RetailMinusInternal = "RetailMinusInternal",
  RetailMinusRetail = "RetailMinusRetail",
}
